import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { enpointCervinoWeb } from 'src/app/configuration/enpointsCervino';
import { ItemFilterModel } from 'src/app/models/filters/item-filter.model';
import { InvoiceDetailModel } from 'src/app/models/invoicing/invoice-detail.model';
import { InvoiceModel } from 'src/app/models/invoicing/invoice.model';
import { RentalStockModel } from 'src/app/models/rental/rental-stock-response.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { FilterGlobalResponseModel } from 'src/app/models/responses/filter-global-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { ArticleSelectionTableModel } from 'src/app/models/tables/archive/article-selection-table.model';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class InvoiceDetailService {

  private readonly _httpClient = inject(HttpClient);

  createDetailFromSalesRemissionAsync(invoiceId: number, conceptInvoicingId: number, salesRemissionIds: number[]): Observable<InvoiceModel> {
    const params = new HttpParams().set('invoiceId', invoiceId).set('conceptInvoicingId', conceptInvoicingId);
    return this._httpClient.post<InvoiceModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoiceDetail.CreateDetailFromSalesRemissionAsync}`,
      salesRemissionIds, { params: params }
    );
  }

  createInvoiceDetail(invoiceDetailContract: InvoiceDetailModel): Observable<InvoiceDetailModel> {
    return this._httpClient.post<InvoiceDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoiceDetail.CreateInvoiceDetail}`,
      invoiceDetailContract
    );
  }

  deleteSomeDetails(invoiceDetailIds: Array<number>): Observable<boolean> {
    return this._httpClient.put<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoiceDetail.DeleteSomeDetails}`,
      invoiceDetailIds
    );
  }

  exportDetailConsultation(filters: ColumnResponseModel[], columnSort: string | null, ascendant: boolean, storeConsolidated: boolean): Observable<Blob> {
    const params = new HttpParams().set('columnSort', columnSort ?? '').set('ascendant', ascendant.toString()).set('storeConsolidated', storeConsolidated);
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoiceDetail.ExportDetailConsultation}`, filters, {
      params, responseType: 'blob',
    });
  }

  exportInvoiceDetails(invoiceId: number): Observable<Blob> {
    const params = new HttpParams().set('invoiceId', invoiceId);
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoiceDetail.ExportInvoiceDetailsAsync}`,
      null, { params, responseType: 'blob' }
    );
  }

  getAllConceptInvoicingForSelectAsync(): Observable<Array<any>> {
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoiceDetail.GetAllConceptInvoicingForSelectAsync}`,);
  }

  getAllInvoiceDetailsWithPagination(page: number, limit: number, columnSort: string | null, ascendant: boolean, storeConsolidated: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<any>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant).set('storeConsolidated', storeConsolidated);
    return this._httpClient.post<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoiceDetail.GetAllInvoiceDetailsWithPagination}`,
      filters, { params: params }
    );
  }

  getAllStoreForSelectAsync(): Observable<Array<any>> {
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoiceDetail.GetAllStoreForSelectAsync}`,);
  }

  getArticleById(articleId: number): Observable<any> {
    return this._httpClient.get<any>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoiceDetail.GetArticleByIdAsync}/${articleId}`);
  }

  getArticlesForInvoices(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<ArticleSelectionTableModel>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);
    return this._httpClient.post<PagedResponseModel<ArticleSelectionTableModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoiceDetail.GetArticlesForInvoicesAsync}`,
      filters, { params: params }
    );
  }

  getColumnsForArticles(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoiceDetail.GetColumnsForArticlesAsync}`);
  }

  getColumnsForDetailConsultation(): Observable<FilterGlobalResponseModel> {
    return this._httpClient.get<FilterGlobalResponseModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoiceDetail.GetColumnsForDetailConsultation}`);
  }

  getColumnsForDetailsInvoice(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoiceDetail.GetColumnsForDetailsInvoiceAsync}`);
  }

  getInvoiceDetailById(invoiceDetailId: number): Observable<InvoiceDetailModel> {
    return this._httpClient.get<InvoiceDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoiceDetail.GetInvoiceDetailById}/${invoiceDetailId}`);
  }

  getInvoiceDetailByKeys(itemFilters: Array<ItemFilterModel>): Observable<InvoiceDetailModel> {
    return this._httpClient.post<InvoiceDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoiceDetail.GetInvoiceDetailByKeys}`,
      itemFilters
    );
  }

  geInvoicetDetailByInvoiceId(invoiceId: number): Observable<Array<InvoiceDetailModel>> {
    return this._httpClient.get<Array<InvoiceDetailModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoiceDetail.GetInvoiceDetailsByInvoiceId}/${invoiceId}`);
  }

  getInvoicesDetailsByInvoiceIdWithPagination(page: number, limit: number, columnSort: string | null, ascendant: boolean, invoiceId: number): Observable<PagedResponseModel<any>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant).set('invoiceId', invoiceId);
    return this._httpClient.get<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoiceDetail.GetInvoicesDetailsByInvoiceIdWithPagination}`,
      { params }
    );
  }

  getListStocksInShop(rentalStockContract: InvoiceModel): Observable<Array<RentalStockModel>> {
    return this._httpClient.post<Array<RentalStockModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoiceDetail.GetListStocksInShop}`,
      rentalStockContract
    );
  }

  getPriceArticleByConceptInvoicing(invoiceId: number, conceptInvoicingId: number, articleId: number): Observable<number> {
    return this._httpClient.get<number>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoiceDetail.GetPriceArticleByConceptInvoicing}/${invoiceId}/${conceptInvoicingId}/${articleId}`);
  }

  getStocksByArticleIdStoreId(rentalStockModel: RentalStockModel): Observable<number> {
    return this._httpClient.post<number>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoiceDetail.GetStocksByArticleIdStoreId}`,
      rentalStockModel
    );
  }

  updateInvoiceDetail(invoiceDetailContract: InvoiceDetailModel): Observable<InvoiceDetailModel> {
    return this._httpClient.put<InvoiceDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.invoiceDetail.UpdateInvoiceDetail}`,
      invoiceDetailContract);
  }
}
